import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth"; // Utility for auth-fetching
import { SERVER_URL } from "../config";

interface Invoice {
  id: number;
  medicationName: string;
  pharmacyName: string;
  clinic: { name: string };
  providerName: string | null;
  amountPaid: number;
  patient: {
    name: string;
    address: string;
    email: string;
    state: string;
  };
  createdAt: string;
}

const InvoiceList: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const response = await fetchWithAuth(
          `${SERVER_URL}/api/invoice/clinic-invoices`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch invoices.");
        }

        const data: Invoice[] = await response.json();
        setInvoices(data);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };
  // Function to extract last name from full name
  const getLastName = (fullName: string): string => {
    const nameParts = fullName.split(" ");
    return nameParts.length > 1 ? nameParts[nameParts.length - 1] : fullName;
  };

  // Function to extract city and state from the address
  const getCityState = (address: string): string => {
    const addressParts = address.split(",");
    if (addressParts.length >= 3) {
      return `${addressParts[addressParts.length - 2].trim()}, ${addressParts[
        addressParts.length - 1
      ].trim()}`;
    }
    return address; // Fallback if address format is unexpected
  };

  if (loading) return <p>Loading invoices...</p>;
  if (error) return <p className="error-message">Error: {error}</p>;

  return (
    <div className="invoice-list-container">
      <h2>Invoice List</h2>
      {invoices.length === 0 ? (
        <p>No invoices available.</p>
      ) : (
        <table className="invoice-table">
          <thead>
            <tr>
              <th className="invoice-header">Invoice ID</th>
              <th className="invoice-header">Provider</th>
              <th className="invoice-header">Amount</th>
              <th className="invoice-header">Date</th>
              <th className="invoice-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <React.Fragment key={invoice.id}>
                <tr>
                  <td className="invoice-cell">{invoice.id}</td>
                  <td className="invoice-cell">{invoice.providerName}</td>
                  <td className="invoice-cell">${invoice.amountPaid}</td>
                  <td className="invoice-cell">
                    {new Date(invoice.createdAt).toLocaleString()}
                  </td>
                  <td className="invoice-cell">
                    <button
                      onClick={() => toggleExpand(invoice.id)}
                      className={`toggle-button ${
                        expandedId === invoice.id ? "expanded" : ""
                      }`}
                    >
                      {expandedId === invoice.id ? "Collapse" : "View Details"}
                    </button>
                  </td>
                </tr>
                {expandedId === invoice.id && (
                  <tr>
                    <td colSpan={5} className="expanded-details">
                      <p>
                        <strong>Medication:</strong> {invoice.medicationName}
                      </p>
                      <p>
                        <strong>Pharmacy:</strong> {invoice.pharmacyName}
                      </p>
                      <p>
                        <strong>Amount Paid:</strong> ${invoice.amountPaid}
                      </p>
                      <p>
                        <strong>Provider:</strong>{" "}
                        {invoice.providerName || "N/A"}
                      </p>
                      <p>
                        <strong>Patient:</strong>{" "}
                        {getLastName(invoice.patient.name)} -{" "}
                        {getCityState(invoice.patient.address)}
                      </p>

                      <p>
                        <strong>Clinic:</strong> {invoice.clinic.name}
                      </p>
                      <p>
                        <strong>Date:</strong>{" "}
                        {new Date(invoice.createdAt).toLocaleString()}
                      </p>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default InvoiceList;
